import { useState } from 'react';
import { FormControlLabel, Menu, MenuItem } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { Checkbox } from 'src/components/Checkbox';
import { ChevronIcon } from 'src/components/ChevronIcon';
import { CUSTOM_BUTTON_VARIANT, CustomButton } from 'src/components/CustomButton';
import { TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { TruncatedTypography } from 'src/components/TruncatedTypography';
import { getResourceContentTypeLabel, getVisibleResourceContentTypes } from 'src/data/performance';
import { ROTATE_DIRECTION } from 'src/data/ui';

import { useStyles } from './useStyles';

const MENU_PROPS = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  getContentAnchorEl: null,
};

export const FilterResourceByType = ({
  className,
  classes,
  value,
  onChange,
  disabled,
  typeToAdd,
  shouldIncludeAudioVideoResources,
}) => {
  const styles = useStyles({ classes });

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleChange = (event) => {
    const { name, checked } = event.target;
    if (checked) {
      value.push(name);
    } else {
      const index = value.indexOf(name);
      value.splice(index, 1);
    }

    const alignedValue = value.length ? [...value] : [];

    onChange(alignedValue);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const visibleContentTypes = getVisibleResourceContentTypes(shouldIncludeAudioVideoResources);

  return (
    <>
      <CustomButton
        className={cx(styles.root, className, { [styles.focused]: open || value.length })}
        onClick={handleClick}
        variant={CUSTOM_BUTTON_VARIANT.outlined}
        endIcon={
          <ChevronIcon
            className={styles.chevronIcon}
            direction={open ? ROTATE_DIRECTION.up : ROTATE_DIRECTION.down}
          />
        }
        data-testid="FilterResourceByType"
        disabled={disabled}
      >
        <TruncatedTypography
          className={styles.label}
          variant={TYPOGRAPHY_VARIANT.regular14}
        >
          {value.length ? value.map(getResourceContentTypeLabel).join(', ') : 'Type'}
        </TruncatedTypography>
      </CustomButton>
      <Menu
        classes={{
          paper: cx(styles.menuPaper, { [styles.wider]: !!typeToAdd }),
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        variant="menu"
        {...MENU_PROPS}
      >
        {visibleContentTypes.map((type) => (
          <MenuItem
            key={type}
            className={styles.menuItem}
          >
            <FormControlLabel
              control={
                <Checkbox
                  className={styles.checkbox}
                  checked={value.includes(type)}
                  onChange={handleChange}
                  name={type}
                  color="primary"
                />
              }
              label={
                <TruncatedTypography className={styles.optionLabel}>
                  {getResourceContentTypeLabel(type)}
                </TruncatedTypography>
              }
              disabled={disabled}
            />
          </MenuItem>
        ))}
        {!!typeToAdd && (
          <MenuItem className={styles.menuItem}>
            <FormControlLabel
              control={
                <Checkbox
                  className={styles.checkbox}
                  checked={value.includes(typeToAdd)}
                  onChange={handleChange}
                  name={typeToAdd}
                  color="primary"
                />
              }
              label={
                <TruncatedTypography className={styles.optionLabel}>
                  Messaging bank assets
                </TruncatedTypography>
              }
              disabled={disabled}
            />
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

FilterResourceByType.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape(),
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  typeToAdd: PropTypes.string,
  shouldIncludeAudioVideoResources: PropTypes.bool,
};

FilterResourceByType.defaultProps = {
  className: null,
  classes: null,
  value: [],
  disabled: false,
  typeToAdd: '',
  shouldIncludeAudioVideoResources: false,
};
